import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, RefreshCw, Loader2, X, Clock, Inbox, CalendarDays, AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { RequestRow } from '../components/requests/RequestRow';
import { RequestForm } from '../components/requests/RequestForm';
import apiService from '../services/api';

const STATUS_TABS = ['pending', 'approved', 'rejected', 'cancelled', 'all'];
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', cancelled: 'Withdrawn', all: 'All' };

// Whose requests: the approval inbox, everyone I look after, or my own
const VIEWS = [
  { key: 'inbox', label: 'To approve' },
  { key: 'team',  label: 'Everyone' },
  { key: 'mine',  label: 'Mine' },
];

// When: upcoming = still to come or happening now; past = finished
const TIME_SCOPES = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past',     label: 'Past' },
  { key: 'year',     label: 'This year' },
  { key: 'any',      label: 'Any time' },
];
const STATUS_KEYS = ['pending', 'approved', 'rejected', 'cancelled', 'all'];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dayOf  = r => String(r.from ?? '').slice(0, 10);
const endOf  = r => String(r.to ?? r.from ?? '').slice(0, 10);
const monthKey = r => dayOf(r).slice(0, 7); // yyyy-mm
function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return 'No date';
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

// ── Department lookup ─────────────────────────────────────────
// Builds a Map<employeeId, Set<deptName>> from the departments list
// returned by GET /departments → [{ id, name, members: [{id, ...}] }]
// Also falls back to fields on the employee object itself for compatibility.
function buildEmpDeptMap(departments) {
  const map = new Map(); // employeeId → Set<deptName>
  departments.forEach(dept => {
    if (!dept.name) return;
    (dept.members ?? []).forEach(member => {
      const uid = member.id;
      if (!uid) return;
      if (!map.has(uid)) map.set(uid, new Set());
      map.get(uid).add(dept.name);
    });
  });
  return map;
}

// Get all dept names for a given employee, merging the prebuilt map
// with any fallback fields on the employee object itself.
function getEmpDeptNames(emp, empDeptMap) {
  if (!emp) return new Set();
  const uid   = emp.id;
  const names = new Set(uid && empDeptMap.has(uid) ? empDeptMap.get(uid) : []);
  if (emp.dept)             names.add(emp.dept);
  if (emp.department?.name) names.add(emp.department.name);
  (emp.departments ?? []).forEach(d => {
    if (d.departmentName) names.add(d.departmentName);
    if (d.name)           names.add(d.name);
  });
  return names;
}

export default function Requests() {
  const { employees, isAdmin, approvalScope, isApprover, currentUser, canApprove, summary } = useApp();
  // Company admins: everyone. Managers / named approvers: their people. Everyone: their own.
  const teamOnly = !isAdmin;
  const [view, setView] = useState(null); // set once we know whether they approve anything

  const [requests,    setRequests]   = useState([]);
  const [leaveTypes,  setLeaveTypes] = useState([]);
  const [departments, setDepts]      = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState(null);

  const [showForm,   setShowForm]   = useState(false);
  const [activeTab,  setActiveTab]  = useState('pending');
  const [timeScope,  setTimeScope]  = useState('upcoming');
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [deptFilter, setDeptFilter] = useState('All');
  const searchRef = useRef(null);
  // Width of the scrolling table, so the reject form and history stay in view
  const scrollRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setScrollW(el.clientWidth));
    ro.observe(el);
    setScrollW(el.clientWidth);
    return () => ro.disconnect();
  }, [loading]);
  const pickedDefaultTab = useRef(false);

  // Opened from a link (e.g. the dashboard's "at a glance"):
  // /requests?view=inbox|team|mine&status=pending|approved|…&when=upcoming|past|year|any
  const [searchParams, setSearchParams] = useSearchParams();
  // A link that names the view wins over the default (inbox for approvers, else mine)
  const fromLink = useRef(VIEWS.some(x => x.key === searchParams.get('view')));
  useEffect(() => {
    const v = searchParams.get('view');
    const st = searchParams.get('status');
    const w = searchParams.get('when');
    if (!v && !st && !w) return;
    if (VIEWS.some(x => x.key === v)) setView(v);
    if (STATUS_KEYS.includes(st)) { setActiveTab(st); pickedDefaultTab.current = true; }
    if (TIME_SCOPES.some(x => x.key === w)) setTimeScope(w);
    else if (v === 'inbox') setTimeScope('any');
    if (v === 'inbox' && !st) { setActiveTab('pending'); pickedDefaultTab.current = true; }
    // Tidy the address bar so a refresh or the back button behaves normally
    setSearchParams({}, { replace: true });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ─────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [requestsRes, typesRes, deptsRes] = await Promise.all([
        // Own requests plus everyone whose leave I approve (all, for company admins)
        apiService.getApprovalRequests({ limit: 1000 }),
        apiService.getLeaveTypes(),
        apiService.getDepartments(),
      ]);

      const reqs = requestsRes?.data?.requests ?? requestsRes?.data ?? requestsRes ?? [];
      setRequests(Array.isArray(reqs) ? reqs : []);

      const types = typesRes?.data ?? typesRes ?? [];
      setLeaveTypes(Array.isArray(types) ? types : []);

      const depts = deptsRes?.data ?? deptsRes ?? [];
      setDepts(Array.isArray(depts) ? depts : []);
    } catch (err) {
      console.error('Requests fetch error:', err);
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const onChange = () => fetchAll();
    window.addEventListener('leaveRequestsReload', onChange);
    return () => window.removeEventListener('leaveRequestsReload', onChange);
  }, [fetchAll]);

  // Approvers open on their inbox; everyone else on their own requests
  // (wait until we know what they approve; the scope arrives just after sign-in)
  const autoPicked = useRef(null);
  useEffect(() => {
    if (!currentUser) return;
    if (fromLink.current) return; // opened from a link that chose the view
    if (view && autoPicked.current !== view) return; // they chose
    const next = isApprover ? 'inbox' : 'mine';
    if ((approvalScope || isAdmin) && next !== view) { autoPicked.current = next; setView(next); }
    else if (!view) { autoPicked.current = 'mine'; setView('mine'); }
  }, [isApprover, currentUser, view, approvalScope, isAdmin]);
  const views = isApprover ? VIEWS : VIEWS.filter(v => v.key === 'mine');

  // The department filter only lists departments this person can see requests for
  const deptOptions = useMemo(() => {
    if (!teamOnly) return departments;
    const managed = new Set((approvalScope?.departments ?? []).map(d => d.id));
    return departments.filter(d => managed.has(d.id));
  }, [departments, teamOnly, approvalScope]);

  // On first load, open on "Pending" if anything is waiting, otherwise "All"
  useEffect(() => {
    if (loading || pickedDefaultTab.current || !view) return;
    pickedDefaultTab.current = true;
    if (view !== 'inbox' && !requests.some(r => r.status === 'pending' && (view !== 'mine' || r.empId === currentUser?.id))) setActiveTab('all');
  }, [loading, requests, view, currentUser]);

  // "/" jumps to search, Escape clears it
  useEffect(() => {
    function onKey(e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (showForm) return;
      e.preventDefault();
      searchRef.current?.focus();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  // ── New request submitted ─────────────────────────────────
  function handleRequestAdded(newReq) {
    if (!newReq?.id) return;
    setRequests(prev => {
      if (prev.some(r => r.id === newReq.id)) return prev;
      return [newReq, ...prev];
    });
  }

  // ── Approve / reject updated in place ─────────────────────
  function handleRequestUpdated(updatedReq) {
    setRequests(prev =>
      prev.map(r => r.id === updatedReq.id ? { ...r, ...updatedReq } : r)
    );
  }

  // ── Hard delete — remove row immediately ──────────────────
  function handleRequestDeleted(id) {
    setRequests(prev => prev.filter(r => r.id !== id));
  }

  // Over-balance warnings: the Requests list carries them (balanceCheck); the
  // dashboard summary has them too, so fall back on those if the list doesn't
  const checkById = useMemo(
    () => new Map((summary?.approvals ?? []).filter(a => a.balanceCheck).map(a => [a.id, a.balanceCheck])),
    [summary]
  );
  const rows = useMemo(
    () => requests.map(r => (r.balanceCheck || !checkById.has(r.id) ? r : { ...r, balanceCheck: checkById.get(r.id) })),
    [requests, checkById]
  );

  // ── Filtering ─────────────────────────────────────────────
  const empDeptMap = useMemo(() => buildEmpDeptMap(departments), [departments]);
  const empById = useMemo(() => new Map(employees.map(e => [e.id, e])), [employees]);
  const empFor = useCallback(
    r => r.employee ?? r.emp ?? empById.get(r.empId ?? r.userId),
    [empById]
  );

  const isMine = useCallback(r => (r.empId ?? r.userId) === currentUser?.id, [currentUser]);
  const decidable = useCallback(r => r.status === 'pending' && !isMine(r) && (canApprove ? canApprove(r.empId) : isAdmin), [isMine, canApprove, isAdmin]);
  const inboxCount = useMemo(() => rows.filter(decidable).length, [rows, decidable]);
  // Waiting for me and would take the person over their balance
  const overBalance = useMemo(() => rows.filter(r => decidable(r) && r.balanceCheck?.overdraw), [rows, decidable]);

  // Everything except the status tab, so the tab counts match what you'd see
  const scoped = useMemo(() => {
    const today = todayStr();
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const emp = empFor(r);
      if (view === 'mine' && !isMine(r)) return false;
      if (view === 'inbox' && !decidable(r)) return false;
      if (view === 'team' && isMine(r)) return false;

      if (timeScope === 'upcoming' && endOf(r) < today) return false;
      if (timeScope === 'past'     && endOf(r) >= today) return false;
      if (timeScope === 'year'     && dayOf(r).slice(0, 4) !== today.slice(0, 4)) return false;

      if (typeFilter !== 'All' && r.type !== typeFilter) return false;

      const deptNames = getEmpDeptNames(emp, empDeptMap);
      if (deptFilter !== 'All' && !deptNames.has(deptFilter)) return false;

      // Search — name, leave type, department or note
      if (q) {
        const name = emp ? (emp.name ?? `${emp.firstName ?? ''} ${emp.lastName ?? ''}`).toLowerCase() : '';
        const hay = [name, (r.type ?? '').toLowerCase(), [...deptNames].join(' ').toLowerCase(), (r.note ?? '').toLowerCase()];
        if (!hay.some(h => h.includes(q))) return false;
      }
      return true;
    });
  }, [rows, empFor, timeScope, search, typeFilter, deptFilter, empDeptMap, view, isMine, decidable]);

  const counts = useMemo(() => ({
    all:      scoped.length,
    pending:  scoped.filter(r => r.status === 'pending').length,
    approved: scoped.filter(r => r.status === 'approved').length,
    rejected: scoped.filter(r => r.status === 'rejected').length,
    cancelled: scoped.filter(r => r.status === 'cancelled').length,
  }), [scoped]);

  // Upcoming: soonest first. Past and any time: most recent first.
  const filtered = useMemo(() => {
    const list = activeTab === 'all' || view === 'inbox' ? scoped : scoped.filter(r => r.status === activeTab);
    const dir = timeScope === 'upcoming' ? 1 : -1;
    return [...list].sort((a, b) => dir * dayOf(a).localeCompare(dayOf(b)));
  }, [scoped, activeTab, timeScope, view]);

  // Rows grouped under month headings
  const groups = useMemo(() => {
    const out = [];
    filtered.forEach(r => {
      const k = monthKey(r);
      const last = out[out.length - 1];
      if (last && last.key === k) last.items.push(r);
      else out.push({ key: k, items: [r] });
    });
    return out;
  }, [filtered]);

  // Each leave type's colour from company settings, for the type pill on each row.
  const typeColors = useMemo(
    () => Object.fromEntries(leaveTypes.map(t => [t.name, t.color])),
    [leaveTypes]
  );

  // Requests waiting for my decision, whatever the filters — for the reminder banner
  const pendingTotal = inboxCount;

  const activeFilters = [
    search.trim() && { key: 'search', label: `“${search.trim()}”`, clear: () => setSearch('') },
    typeFilter !== 'All' && { key: 'type', label: typeFilter, clear: () => setTypeFilter('All') },
    deptFilter !== 'All' && { key: 'dept', label: deptFilter, clear: () => setDeptFilter('All') },
  ].filter(Boolean);
  const clearAll = () => { setSearch(''); setTypeFilter('All'); setDeptFilter('All'); };

  const scopeWord = { upcoming: 'upcoming', past: 'past', year: '', any: '' }[timeScope];
  const emptyText = (() => {
    if (activeFilters.length) return 'No requests match these filters.';
    if (view === 'inbox') return 'Nothing waiting for your approval.';
    if (activeTab === 'pending') return `Nothing waiting for approval${scopeWord ? ` in ${scopeWord} leave` : ''}.`;
    if (activeTab === 'approved') return `No approved ${scopeWord} leave.`.replace('  ', ' ');
    if (activeTab === 'rejected') return `No rejected ${scopeWord} requests.`.replace('  ', ' ');
    return timeScope === 'upcoming' ? 'No upcoming leave booked.' : 'No requests yet.';
  })();

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <style>{`
        .rq-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 1 1 auto; min-width: 0; }
        .rq-search { position: relative; flex: 0 1 260px; min-width: 180px; }
        @media (max-width: 640px) { .rq-search { flex: 1 1 100%; } }
        .rq-row2 { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .rq-chips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .rq-chip { display: inline-flex; align-items: center; gap: 4px; height: 26px; padding: 0 6px 0 10px; border-radius: 13px; border: 1px solid var(--border); background: var(--surface); color: var(--text-2); font-size: 12.5px; font-weight: 500; cursor: pointer; }
        .rq-chip:hover { border-color: var(--field-border); color: var(--text); }
        .rq-chip svg { color: var(--text-muted); }
        .rq-link { border: none; background: none; padding: 0 4px; color: var(--accent); font-size: 12.5px; font-weight: 600; cursor: pointer; }
        .rq-banner { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 12px; background: var(--warn-bg-soft); border: 1px solid var(--warn-border); color: var(--warn-text); font-size: 13px; }
        .rq-banner b { font-weight: 700; }
        .rq-banner button { margin-left: auto; height: 30px; padding: 0 12px; border-radius: 8px; border: 1px solid var(--warn-border); background: var(--surface); color: var(--warn-text); font-size: 12.5px; font-weight: 600; cursor: pointer; }
        .rq-banner button:hover { background: var(--warn-bg); }
        .rq-month td { padding: 9px 16px 7px; background: var(--surface-2); border-top: 1px solid var(--border-soft); font-size: 11.5px; font-weight: 700; color: var(--text-2); text-transform: uppercase; letter-spacing: 0.05em; }
        .rq-month td span { margin-left: 8px; font-weight: 600; color: var(--text-muted); text-transform: none; letter-spacing: 0; }
        .rq-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 52px 20px; color: var(--text-muted); font-size: 13.5px; text-align: center; }
        .rq-empty svg { opacity: 0.45; margin-bottom: 4px; }
        /* Narrow screens: the name column and the actions stay put while the middle scrolls */
        .rq-scroll { overflow-x: auto; }
        .rq-table .rq-sticky-l, .rq-table thead th:first-child { position: sticky; left: 0; z-index: 2; background: var(--surface); }
        .rq-table .rq-sticky-r, .rq-table thead th:last-child { position: sticky; right: 0; z-index: 2; background: var(--surface); box-shadow: -8px 0 8px -8px rgba(28,26,20,.18); }
        .rq-table thead th:first-child, .rq-table thead th:last-child { background: var(--surface-2); }
        .rq-table tr:hover .rq-sticky-l, .rq-table tr:hover .rq-sticky-r { background: var(--hover-solid, var(--surface-2)); }
        .rq-table .rq-month td { position: sticky; left: 0; }
        .rq-pinned-panel { position: sticky; left: 0; width: min(100%, calc(var(--rq-w, 100vw) - 40px)); box-sizing: border-box; }
        .rq-kbd { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 4px; border-radius: 4px; border: 1px solid var(--border); background: var(--surface-2); color: var(--text-muted); font-size: 10.5px; font-weight: 600; position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; }
      `}</style>

      {/* Toolbar: search and filters on the left, actions on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="rq-filters">
          <div className="rq-search">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 th-muted" />
            <input
              ref={searchRef}
              type="search"
              className="form-input pl-8"
              style={{ width: '100%', paddingRight: search ? 12 : 34 }}
              placeholder="Search people or types…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur(); } }}
              aria-label="Search requests"
            />
            {!search && <span className="rq-kbd" aria-hidden="true">/</span>}
          </div>

          <select className="form-input" style={{ width: 'auto' }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)} aria-label="Leave type">
            <option value="All">All types</option>
            {leaveTypes.map(t => <option key={t.id ?? t.name} value={t.name}>{t.name}</option>)}
          </select>

          <select className="form-input" style={{ width: 'auto' }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)} aria-label="Department">
            <option value="All">All departments</option>
            {deptOptions.map(d => <option key={d.id ?? d.name} value={d.name}>{d.name}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            disabled={loading}
            className="form-input flex items-center justify-center gap-1.5"
            style={{ width: 40, height: 40, padding: 0, cursor: 'pointer', color: 'var(--text-2)' }}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
            style={{ height: 40, flexShrink: 0 }}
          >
            <Plus size={15} strokeWidth={2.6} /> New request
          </button>
        </div>
      </div>

      {/* Whose requests: my approval inbox, everyone I look after, or mine */}
      {views.length > 1 && (
        <div className="seg" role="group" aria-label="Whose requests" style={{ alignSelf: 'flex-start' }}>
          {views.map(v => (
            <button key={v.key} type="button" aria-pressed={view === v.key} onClick={() => { setView(v.key); if (v.key === 'inbox') { setActiveTab('pending'); setTimeScope('any'); } }}>
              {v.key === 'team' ? (isAdmin ? 'Everyone' : 'My team') : v.label}
              {v.key === 'inbox' && <span className="seg-count" style={inboxCount ? { background: '#FFC81E', color: '#1C1A14' } : undefined}>{inboxCount}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Status and time */}
      <div className="rq-row2">
        {view !== 'inbox' ? (
          <div className="seg" role="group" aria-label="Filter by status">
            {STATUS_TABS.map(tab => (
              <button
                key={tab}
                type="button"
                aria-pressed={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {STATUS_LABEL[tab]}
                <span className="seg-count">{counts[tab]}</span>
              </button>
            ))}
          </div>
        ) : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Requests waiting for your decision, soonest first.</span>}

        <div className="seg" role="group" aria-label="Filter by date">
          {TIME_SCOPES.map(s => (
            <button key={s.key} type="button" aria-pressed={timeScope === s.key} onClick={() => setTimeScope(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active filters, each removable */}
      {activeFilters.length > 0 && (
        <div className="rq-chips">
          {activeFilters.map(f => (
            <button key={f.key} type="button" className="rq-chip" onClick={f.clear} title="Remove filter">
              {f.label} <X size={13} />
            </button>
          ))}
          {activeFilters.length > 1 && <button type="button" className="rq-link" onClick={clearAll}>Clear all</button>}
        </div>
      )}

      {/* Reminder when requests are waiting but you're looking elsewhere */}
      {!loading && pendingTotal > 0 && view !== 'inbox' && (
        <div className="rq-banner" role="status">
          <Clock size={15} />
          <span><b>{pendingTotal}</b> {pendingTotal === 1 ? 'request is' : 'requests are'} waiting for approval</span>
          <button type="button" onClick={() => { setView('inbox'); setActiveTab('pending'); setTimeScope('any'); clearAll(); }}>Review</button>
        </div>
      )}

      {/* Requests that would take someone over their balance */}
      {!loading && overBalance.length > 0 && (
        <div className="rq-banner" role="status" style={{ background: 'var(--bad-bg)', borderColor: 'var(--bad-border)', color: 'var(--bad-text)' }}>
          <AlertTriangle size={15} />
          <span>
            <b>{overBalance.length}</b> {overBalance.length === 1 ? 'request' : 'requests'} waiting for you would take {overBalance.length === 1 ? 'someone' : 'people'} over their balance
            {overBalance.length <= 3 ? `: ${[...new Set(overBalance.map(r => { const e = empFor(r); return e ? (e.firstName ?? (e.name ?? '').split(' ')[0]) : 'someone'; }))].join(', ')}` : ''}.
            {' '}Talk to them before approving.
          </span>
          {view !== 'inbox' && (
            <button type="button" style={{ borderColor: 'var(--bad-border)', color: 'var(--bad-text)' }} onClick={() => { setView('inbox'); setActiveTab('pending'); setTimeScope('any'); clearAll(); }}>Review</button>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{ padding: '10px 14px', background: 'var(--bad-bg)', border: '1px solid var(--bad-border)', borderRadius: 12, fontSize: 13, color: 'var(--bad-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{error}</span>
          <button onClick={fetchAll} style={{ fontSize: 12, color: 'var(--bad-text)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60, gap: 10, color: 'var(--text-muted)' }}>
            <Loader2 size={20} className="animate-spin" />
            <span style={{ fontSize: 14 }}>Loading requests…</span>
          </div>
        ) : (
          <>
            <div className="rq-scroll" ref={scrollRef} style={{ '--rq-w': `${scrollW}px` }}>
              <table className="w-full rq-table" style={{ minWidth: 760 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                    {['Employee', 'Type', 'Dates', 'Days', 'Status', 'Note', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[11px] font-bold th-muted uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--border-soft)]">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="rq-empty">
                          {activeTab === 'pending' && !activeFilters.length ? <Inbox size={24} /> : <CalendarDays size={24} />}
                          <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{emptyText}</span>
                          {activeFilters.length > 0 ? (
                            <button type="button" className="rq-link" onClick={clearAll}>Clear filters</button>
                          ) : timeScope !== 'any' ? (
                            <button type="button" className="rq-link" onClick={() => setTimeScope('any')}>Show any time</button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    groups.map(g => (
                      <React.Fragment key={g.key}>
                        <tr className="rq-month">
                          <td colSpan={7}>
                            {monthLabel(g.key)}
                            <span>{g.items.length} {g.items.length === 1 ? 'request' : 'requests'}</span>
                          </td>
                        </tr>
                        {g.items.map(r => (
                          <RequestRow
                            key={r.id}
                            request={r}
                            typeColor={typeColors[r.type]}
                            onUpdated={handleRequestUpdated}
                            onDeleted={handleRequestDeleted}
                          />
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 text-xs th-muted" style={{ borderTop: '1px solid var(--border-soft)' }}>
              Showing {filtered.length} of {requests.length} requests
              {timeScope === 'upcoming' && ' · soonest first'}
              {timeScope !== 'upcoming' && ' · most recent first'}
            </div>
          </>
        )}
      </div>

      {/* New request form */}
      {showForm && (
        <RequestForm
          onClose={() => setShowForm(false)}
          onRequestCreated={handleRequestAdded}
        />
      )}
    </div>
  );
}